import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
} from "lucide-react";
import {
  API,
  fmtDate,
} from "./shared";

export function PaymentsTab({ authHeader }) {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);

  const loadOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/supplement-orders`, { headers: authHeader() });
      setOrders(res.data || []);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/payments`, { headers: authHeader() });
        setData(res.data);
      } catch {}
    })();
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOrderStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/supplement-orders/${id}/status`, { status }, { headers: authHeader() });
      loadOrders();
    } catch {}
  };

  const ORDER_CLS = {
    paid: "bg-[#AE8F78]/15 text-[#875F45] border-[#AE8F78]/40",
    processing: "bg-[#3F5043]/10 text-[#3F5043] border-[#3F5043]/30",
    shipped: "bg-[#899480]/15 text-[#3F5043] border-[#899480]/40",
    delivered: "bg-[#899480]/25 text-[#3F5043] border-[#899480]/50",
    pending_payment: "bg-[#AE8F78]/10 text-[#875F45]/70 border-[#AE8F78]/20",
    canceled: "bg-red-100 text-red-700 border-red-200",
  };

  const STATUS_CLS = {
    paid: "bg-[#3F5043]/10 text-[#3F5043] border-[#3F5043]/30",
    pending: "bg-[#AE8F78]/15 text-[#875F45] border-[#AE8F78]/40",
    failed: "bg-[#875F45]/10 text-[#875F45] border-[#875F45]/30",
    expired: "bg-[#899480]/10 text-[#899480] border-[#899480]/30",
  };

  if (!data) {
    return (
      <div className="text-center py-16 text-[#899480]">
        <Loader2 className="animate-spin inline" size={22} />
      </div>
    );
  }
  const s = data.summary;

  return (
    <div data-testid="payments-tab" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Membership revenue", value: `$${s.total_revenue_usd.toFixed(2)}`, testid: "pay-stat-revenue" },
          { label: "Active members", value: s.active_members, testid: "pay-stat-members" },
          { label: "Completed payments", value: s.paid_transactions, testid: "pay-stat-paid" },
          { label: "Stripe mode", value: s.mode === "live" ? "LIVE" : "Test", testid: "pay-stat-mode" },
        ].map((c) => (
          <div key={c.label} data-testid={c.testid}
            className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">{c.label}</div>
            <div className="font-serif text-3xl text-[#3F5043]">{c.value}</div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="font-serif text-2xl text-[#3F5043] mb-4">Transactions</h3>
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#EDEDEE] text-left text-[10px] uppercase tracking-[0.18em] text-[#899480]">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody data-testid="payments-table">
              {data.transactions.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#3F5043]/60">No transactions yet.</td></tr>
              )}
              {data.transactions.map((t, i) => (
                <tr key={t.session_id} data-testid={`payment-row-${i}`} className="border-t border-[#BCB3A6]/20">
                  <td className="px-4 py-3 text-[#3F5043]">{t.client_email}</td>
                  <td className="px-4 py-3 text-[#3F5043]/70">{t.lookup_key === "membership_monthly" ? "Membership (monthly)" : t.lookup_key}</td>
                  <td className="px-4 py-3 text-[#3F5043]">${Number(t.amount).toFixed(2)} {String(t.currency || "").toUpperCase()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-2.5 py-1 rounded-full border ${STATUS_CLS[t.payment_status] || STATUS_CLS.pending}`}>
                      {t.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#3F5043]/60">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-[#3F5043]/50">
          Consultations, support packages and interpreter sessions are booked and paid natively on the /book page via Stripe, and appear here. Memberships ($59.99/mo) also run through Stripe.
        </p>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-[#3F5043] mb-4">Supplement orders</h3>
        <div data-testid="supplement-orders" className="space-y-3">
          {orders.length === 0 && (
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#3F5043]/60">
              No supplement orders yet.
            </div>
          )}
          {orders.map((o, i) => (
            <div key={o.id} data-testid={`order-row-${i}`}
              className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-serif text-lg text-[#3F5043]">
                    {o.full_name} — ${Number(o.total).toFixed(2)}
                  </div>
                  <div className="text-xs text-[#3F5043]/60 mt-1">
                    {o.items.map((it) => `${it.qty}× ${it.name}`).join(" · ")}
                  </div>
                  <div className="text-xs text-[#3F5043]/60 mt-1">
                    {o.client_email}{o.phone ? ` · ${o.phone}` : ""} — ship to: {o.shipping?.address1}, {o.shipping?.city}, {o.shipping?.state} {o.shipping?.zip}
                  </div>
                  <div className="text-[11px] text-[#3F5043]/40 mt-1">{fmtDate(o.created_at)}</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border ${ORDER_CLS[o.status] || ORDER_CLS.pending_payment}`}>
                    {o.status === "pending_payment" ? "unpaid" : o.status}
                  </span>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {o.status === "paid" && (
                      <button onClick={() => setOrderStatus(o.id, "processing")} data-testid={`order-processing-${i}`}
                        className="px-3 py-1.5 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs hover:bg-[#875F45] transition-colors">
                        Processing
                      </button>
                    )}
                    {o.status === "processing" && (
                      <button onClick={() => setOrderStatus(o.id, "shipped")} data-testid={`order-shipped-${i}`}
                        className="px-3 py-1.5 rounded-full bg-[#899480] text-white text-xs hover:bg-[#3F5043] transition-colors">
                        Mark shipped
                      </button>
                    )}
                    {o.status === "shipped" && (
                      <button onClick={() => setOrderStatus(o.id, "delivered")} data-testid={`order-delivered-${i}`}
                        className="px-3 py-1.5 rounded-full bg-[#899480] text-white text-xs hover:bg-[#3F5043] transition-colors">
                        Delivered
                      </button>
                    )}
                    {["paid", "processing"].includes(o.status) && (
                      <button onClick={() => setOrderStatus(o.id, "canceled")} data-testid={`order-cancel-${i}`}
                        className="px-3 py-1.5 rounded-full bg-[#EDEDEE] text-[#875F45] text-xs hover:bg-[#875F45] hover:text-[#F8F7F3] transition-colors">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
